import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';

const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
const COPYRIGHT_YEAR = new Date().getUTCFullYear();

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) {
    const to = location.state?.from?.pathname ?? '/dashboard';
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username.trim(), password);
      navigate(location.state?.from?.pathname ?? '/dashboard', { replace: true });
    } catch (err) {
      setError(err?.data?.error ?? err.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-ink-50 p-6">
      <div className="card w-full max-w-md overflow-hidden">
        <div className="p-8">
          <div className="mb-7 flex flex-col items-center text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-ink-50 ring-1 ring-ink-100">
              <img src="/icons/logo.svg" alt="MVT ADMS" className="h-14 w-14" />
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 ring-1 ring-brand-100">
              <i className="fa-solid fa-shield-halved" />
              MVT ADMS
            </span>
            <h1 className="mt-3 text-2xl font-bold text-ink-900">Map Vector Tile Server</h1>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink-800">Username</label>
              <input
                className="input py-2.5"
                autoComplete="username"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink-800">Password</label>
              <input
                className="input py-2.5"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}
            <button
              type="submit"
              className="btn w-full bg-ink-900 py-3 text-white hover:bg-ink-800"
              disabled={loading}
            >
              {loading ? (
                <><i className="fa-solid fa-spinner fa-spin-pulse" /> Signing in…</>
              ) : (
                <><i className="fa-solid fa-right-to-bracket" /> Sign In</>
              )}
            </button>
          </form>
        </div>

        <div className="flex items-center justify-between border-t border-ink-100 bg-white px-8 py-4 text-xs text-ink-500">
          <span>&copy; {COPYRIGHT_YEAR} MicroGIS&trade;</span>
          <span>Build v{APP_VERSION}</span>
        </div>
      </div>
    </div>
  );
}
